/**
 * Sprint T-17.1 · Notification Center · DeliveriesTab.
 * Tabla con filtros + drawer lateral.
 * §4.2 · filters status/channel/template/tenant/intent + paginación 25 · drawer 480px.
 */
import React, { useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, MetricCard, WidgetGrid, CitationBadge } from "../advisor";
import { useDeliveries, useTemplates } from "../../hooks/useNotifications";
import DeliveryRow from "./shared/DeliveryRow";
import DeliveryDrawer from "./shared/DeliveryDrawer";
import { ALL_CHANNELS } from "./shared/ChannelIcon";
import { ALL_DELIVERY_STATUSES } from "./shared/StatusPill";

const PAGE_SIZES = [25, 50, 100];

function FilterBar({ filters, setFilters, templates }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 0 }));
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="notif-deliveries-filters">
      <select
        data-testid="filter-status"
        value={filters.status || ""}
        onChange={(e) => set("status", e.target.value || undefined)}
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-[11px]"
      >
        <option value="">Todos los estados</option>
        {ALL_DELIVERY_STATUSES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <select
        data-testid="filter-channel"
        value={filters.channel || ""}
        onChange={(e) => set("channel", e.target.value || undefined)}
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-[11px]"
      >
        <option value="">Todos los canales</option>
        {ALL_CHANNELS.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <select
        data-testid="filter-template"
        value={filters.template_id_hint || ""}
        onChange={(e) => set("template_id_hint", e.target.value || undefined)}
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-[11px]"
      >
        <option value="">Todos los templates</option>
        {(templates?.items || []).map((t) => (
          <option key={t.template_id} value={t.template_id}>{t.template_id}</option>
        ))}
      </select>
      <input
        type="text"
        data-testid="filter-tenant"
        value={filters.tenant_id || ""}
        onChange={(e) => set("tenant_id", e.target.value || undefined)}
        placeholder="tenant_id"
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 font-mono text-[11px]"
      />
      <input
        type="text"
        data-testid="filter-intent"
        value={filters.intent_id || ""}
        onChange={(e) => set("intent_id", e.target.value || undefined)}
        placeholder="intent_id"
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 font-mono text-[11px]"
      />
      <select
        data-testid="filter-limit"
        value={filters.limit}
        onChange={(e) => set("limit", Number(e.target.value))}
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-[11px]"
      >
        {PAGE_SIZES.map((n) => (
          <option key={n} value={n}>{n} / página</option>
        ))}
      </select>
    </div>
  );
}

export default function DeliveriesTab() {
  const [filters, setFilters] = useState({
    status: undefined,
    channel: undefined,
    tenant_id: undefined,
    intent_id: undefined,
    template_id_hint: undefined,   // client-side hint (no backend param)
    limit: 25,
    page: 0,
  });
  const [selected, setSelected] = useState(null);

  const q = useDeliveries({
    status: filters.status,
    channel: filters.channel,
    tenant_id: filters.tenant_id,
    intent_id: filters.intent_id,
    limit: filters.limit,
    skip: filters.page * filters.limit,
  });
  const templates = useTemplates();

  // Optional client-side filter by template_id_hint
  const itemsAll = q.data?.items || [];
  const items = filters.template_id_hint
    ? itemsAll.filter((d) => d.template_id === filters.template_id_hint)
    : itemsAll;
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / filters.limit));

  return (
    <section className="space-y-4" data-testid="notif-deliveries-tab">
      <WidgetGrid cols="md:grid-cols-3">
        <MetricCard label="Total deliveries" value={total} testId="notif-deliveries-total" hint="con filtros" />
        <MetricCard label="Página" value={`${filters.page + 1} / ${totalPages}`} testId="notif-deliveries-page" hint={`${filters.limit} por página`} />
        <MetricCard
          label="Filtros activos"
          value={
            [filters.status, filters.channel, filters.tenant_id, filters.intent_id, filters.template_id_hint]
              .filter(Boolean).length
          }
          testId="notif-deliveries-filter-count"
          hint="incluyendo cliente"
        />
      </WidgetGrid>

      <WidgetCard
        title="Deliveries"
        description="Tabla auditada · cada intento persistido · click en fila para ver detalle"
        citations={["/api/notifications/deliveries"]}
        testId="notif-deliveries-list"
        actions={
          <button
            type="button"
            data-testid="notif-deliveries-refresh"
            onClick={() => q.refetch()}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
          >
            <RefreshCw className={`h-3 w-3 ${q.isFetching ? "animate-spin" : ""}`} /> Refrescar
          </button>
        }
      >
        <FilterBar filters={filters} setFilters={setFilters} templates={templates.data} />

        {q.isLoading && <div className="mt-3"><LoadingState /></div>}
        {q.error && <EmptyState message="No se pudo cargar deliveries" testId="notif-deliveries-error" />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="Sin resultados"
            message="Sin deliveries con los filtros actuales."
            testId="notif-deliveries-empty"
          />
        )}
        {items.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left" data-testid="notif-deliveries-table">
              <thead className="border-b border-[var(--ag-border)] text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                <tr>
                  <th className="px-2 py-2">Finished</th>
                  <th className="px-2 py-2">Canal</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Destinatario</th>
                  <th className="px-2 py-2">Template</th>
                  <th className="px-2 py-2">Provider</th>
                  <th className="px-2 py-2 text-right">Attempt</th>
                  <th className="px-2 py-2 text-right">Latency</th>
                  <th className="px-2 py-2">Mode</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((d, i) => (
                  <DeliveryRow key={d.id} delivery={d} onSelect={setSelected} index={i} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > filters.limit && (
          <div className="mt-3 flex items-center justify-between text-xs" data-testid="notif-deliveries-pagination">
            <span className="text-[var(--ag-text-dim)]">
              Mostrando {filters.page * filters.limit + 1}
              –{Math.min((filters.page + 1) * filters.limit, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="notif-deliveries-prev"
                onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, f.page - 1) }))}
                disabled={filters.page === 0}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </button>
              <span className="px-2 text-[10px] tabular-nums">
                pág {filters.page + 1} / {totalPages}
              </span>
              <button
                type="button"
                data-testid="notif-deliveries-next"
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                disabled={(filters.page + 1) * filters.limit >= total}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                Siguiente <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </WidgetCard>

      {selected && (
        <DeliveryDrawer delivery={selected} onClose={() => setSelected(null)} />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["Cada delivery audita T-14 · attempt-level log persistido"]} />
      </footer>
    </section>
  );
}
